import React, { useEffect, useState } from 'react';
import {
    ColumnDef,
    OnChangeFn,
    RowData,
    SortingState,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table'
import classNames from 'classnames';
import i18next from 'i18next';
import PaginationComponent from './PaginationComponent';
import { LIMIT_PAGINATE } from '@/constants/default';

declare module '@tanstack/react-table' {
    interface ColumnMeta<TData extends RowData, TValue> {
        className?: string
    }
}

interface TableProps<TData> {
    data: TData[]
    columns: ColumnDef<TData>[]
    pageSize?: number
    sort?: SortingState
    initialSortBy?: SortingState
    className?: string
    isLoading: boolean
    isPagination?: boolean
    currentPage?: number
    totalPages?: number
    titleNone?: string
    onChangePage?: (page: number) => void
    onChangeSort?: (sort: SortingState) => void
}

const TableComponent = <TData extends object>({data, columns, sort = [], pageSize=LIMIT_PAGINATE, className = "w-full table-auto", isLoading, isPagination = false, currentPage=0, totalPages=0, titleNone =  i18next.t("table.none"), onChangePage, onChangeSort}: TableProps<TData>) => {
    const memoizedData = React.useMemo(() => data, [data]);
    const memoizedColumns = React.useMemo(() => columns, [columns]);
    const [sorting, setSorting] = useState<SortingState>([])

    const table = useReactTable({
        data: memoizedData,
        columns: memoizedColumns,
        state: {
            sorting: sorting.length > 0 ? sorting : sort,
            pagination: {
                pageIndex: 0,
                pageSize: pageSize,
            }
        },
        enableSortingRemoval: false,
        manualSorting: true,
        manualPagination: true,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
    })

    useEffect(() => {
        if(sorting.length > 0) {
            onChangeSort && onChangeSort(sorting)
        }
    }, [sorting])

    return (
        <>
            <table className={classNames(className)}>
                <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id} className="text-left">
                            {headerGroup.headers.map(header => (
                                <th key={header.id} className={`py-2 px-2 font-medium ${header?.column?.columnDef?.meta?.className || ""}`}>
                                    {header.isPlaceholder ? null : (
                                        <div
                                            {...{
                                                className: header.column.getCanSort()
                                                ? 'cursor-pointer select-none'
                                                : '',
                                                onClick: header.column.getToggleSortingHandler(),
                                            }}
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                            {{
                                                asc: <i className="fa-light fa-caret-up fa-sm ms-2" />,
                                                desc: <i className="fa-light fa-caret-down fa-sm ms-2" />,
                                            }[header.column.getIsSorted() as string] ?? null}
                                        </div>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {
                        isLoading && <tr>
                            <td className="border-b border-[#eee] py-2 px-2 dark:border-strokedark" colSpan={table.getHeaderGroups()[0]?.headers?.length}>
                                <div className="flex justify-center">
                                    <div className="dot-loader bg-slate-700 dark:bg-gray-200"></div>
                                    <div className="dot-loader bg-slate-700 dark:bg-gray-200"></div>
                                    <div className="dot-loader bg-slate-700 dark:bg-gray-200"></div>
                                </div>
                            </td>
                        </tr>
                    }
                    {
                        table.getRowModel().rows?.length > 0 ?
                            table.getRowModel().rows.map((row, index) => (
                                // <tr key={row.id} className={`${index % 2 === 0 && "bg-gray-2 dark:bg-meta-4"} ${(row.original as any)?.status === 1 ? 'bg-slate-400 dark:bg-slate-800' : ''}`}>
                                <tr key={row.id} className={`${index % 2 === 0 && "bg-gray-2 dark:bg-meta-4"}`}>
                                    {row.getVisibleCells().map(cell => (
                                    <td key={cell.id} className={`border-b border-[#eee] py-2 px-2 dark:border-strokedark ${cell.column.columnDef.meta?.className || ""}`}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                    ))}
                                </tr>
                            ))
                            :
                            <tr className="bg-gray-2 dark:bg-meta-4">
                                <td className="text-center border-b border-[#eee] py-2 px-2 dark:border-strokedark" colSpan={table.getHeaderGroups()[0].headers?.length} >{titleNone}</td>
                            </tr>
                    }
                </tbody>
            </table>
            {
                isPagination &&
                    <PaginationComponent
                        key={"pagiantion"}
                        pageIndex={currentPage}
                        pageCount={totalPages}
                        onChangePage={page => onChangePage && onChangePage(page)}
                        isLoading={isLoading}
                    />
            }
        </>
    )
}

export default TableComponent;
